import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// `avatarColor` bleibt aus dem Chat — die Farbe ist dort wie hier eine reine
// Ableitung aus dem Schlüssel. Das KÜRZEL kommt seit dem 22.09.2026 aus
// `kuerzelVon` (PO: „bei den Notizen bitte die Kürzel anzeigen, nicht die
// eigenen Abbreviations"). `initials` aus dem Chat bleibt unangetastet: Dort
// stehen oft Gesprächspartner ohne KMVS-Konto, für die es kein Kürzel gibt.
import { avatarColor } from "../chat/chatTheme";
import { kuerzelVon } from "../vorgang/shared/people";
import { findUserBy, labelForUserKey, type AppUser } from "../../services/userService";
import type { NoteRefArt, NoteRefDto } from "../../services/vorgangService";
import { mentionToken } from "../notes/mentions";
import { behandleListenTaste } from "../mail/richtext/listenTasten";
import { useRechtschreibung } from "../mail/richtext/rechtschreibung/useRechtschreibung";
import { AuswahlAnker } from "./AuswahlAnker";
import { BezugPicker } from "./BezugPicker";
import { MentionPicker } from "./MentionPicker";
import { erwaehnteIn, neuErwaehnt, vereinigeMentions } from "./erwaehnungen";
import { behandleAuswahlTastatur } from "./auswahlTastatur";
import { BlockKontextmenue, type BlockAktion } from "./BlockKontextmenue";
import { aktivesToken, bezugEinsetzen, mentionEinsetzen, type AktivesToken } from "./bezugEinsetzen";
import { entwaesserBezuege, hydratisiereBezuege } from "./bezugDarstellung";
import { entwaesserBilder, gibBilderFrei, hydratisiereBilder } from "./bildDarstellung";
import {
    bereinigeEingefuegtesHtml,
    auswahlAnPunkt,
    bildDateienAus,
    einfuegeBildDateien,
    bilderAusQuellen,
    verworfeneBildQuellen,
    bildHochladenUndEinsetzen,
    htmlEinsetzenAnAuswahl,
    linkEinsetzen,
    linkUnter,
    traegtDateien,
    urlAusText,
} from "./einfuegen";
import type { BezugKandidat } from "./bezugKandidaten";
import { bloeckeAus, istLeererBlocktext, notizSchluessel, vereinigeRefs, type Block, type BlockNotiz } from "./blockModell";
import { KommentarMarke } from "./KommentarMarke";
import { AnmerkFaden } from "./AnmerkFaden";
import { MarkierLeiste } from "./MarkierLeiste";
import {
    KLASSE_ANMERK,
    KLASSE_GETAN,
    neuerAnker,
    stelleUnter,
    umwickle,
    wickleAus,
} from "./markieren";
import type { Kommentar } from "./kommentarModell";
import type { KommentarAendern } from "./KommentarFaden";
import { KommentarEingabe } from "./KommentarEingabe";
import type { KommentarZusatz } from "./kommentarInhalt";
import { NebenStelle } from "./NebenStelle";
import { erledigteStellenCss, useMarkierungFertig } from "./markierungFertig";
import { istLeer, reinigeBlockHtml } from "./blockHtml";
import { merkeSchritt, mitVerlauf, rueckgaengig } from "./formatVerlauf";
import { BildLeiste } from "./BildLeiste";
import { entferneBild, feldInnenBreite, setzeBildBreite, stufeBildBreite } from "./bildBearbeiten";
import { abschnitteAus, hoverTitel, NEU, aenderungen, zeitpunkt, type Abschnitt } from "./notizFeldModell";
import {
    anAbschnittsGrenze,
    baueFeld,
    entsperreAnZeilengrenze,
    fokussierterKey,
    gleicheAb,
    kinderVon,
    loescheAuswahlUeberAbschnitte,
    leseAbschnitte,
    mitSchluesselErhalten,
    normalisiere,
    sperreEingesetztes,
} from "./notizFeldDom";
import { DiktatKnopf } from "../diktat/DiktatKnopf";
import { auswahlIn, inEditableEinfuegen } from "../diktat/diktatEinfuegen";
import "./notizbloecke.css";
import { aktiveAuswahl, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * DIE NOTIZEN EINES ARBEITSSTRANGS ALS EIN TEXTFELD (PO 18.09.2026: „Die
 * Notizen müssen ein Block sein — die Notizen einfach zusammen in einem Block
 * mit Zeilenumbruch zeigen"; am 20.09.2026 bestätigt).
 *
 * ══ EIN CURSOR ÜBER ALLES ══════════════════════════════════════════════════
 * Bis hierher war jede Notizzeile ein eigenes `contentEditable`
 * (`NotizBlock`). #1681 hat die Naht dazwischen optisch entfernt — der Cursor
 * blieb trotzdem an jeder Grenze hängen, und eine Markierung reichte nie über
 * zwei Notizen. Jetzt ist es EIN Feld: Pfeiltasten, Rücktaste, Markieren und
 * Einfügen laufen durch.
 *
 * GESPEICHERT wird weiter EINE Zeile je Abschnitt — Regeln in
 * `notizFeldModell.ts`, DOM-Handgriffe in `notizFeldDom.ts`. An der einzelnen
 * Zeile hängen Archiv, Suche, Erwähnungen, Kommentare und die Frage, wer
 * geschrieben hat; ein Dokument je Strang verlöre all das.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ WER GESCHRIEBEN HAT, STEHT AM RAND ═════════════════════════════════════
 * „Es reicht, wenn das Kürzel da steht und dann die Uhrzeit, das Datum … oder
 * ich gehe einfach da drüber" (PO 18.09.2026). Das Kürzel liegt LINKS
 * AUSSERHALB des Textes (CSS `::before` aus `data-kuerzel`), der Name kommt
 * beim Überfahren. Das Datum steht seit T-407 (27.10.2026) klein in der
 * Leerzeile über der Notiz (CSS `::after` aus `data-zeit`) — es kostet keine
 * eigene Zeile, die Leerzeile ist ohnehin da.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ UNCONTROLLED ═══════════════════════════════════════════════════════════
 * React rendert NIE in das Feld hinein — ein controlled `contentEditable`
 * setzt bei jedem Durchlauf `innerHTML` neu und reißt den Cursor an den
 * Anfang. Gebaut wird einmal beim Montieren; was von außen kommt, trägt
 * `gleicheAb` ein, ohne den Abschnitt anzufassen, in dem gerade jemand
 * schreibt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Ruhe vor dem Speichern — wie am Notizblock (Vorbild TerminNotizenPage). */
const SPEICHER_RUHE_MS = 1200;

/**
 * Spätestens nach dieser Zeit wird gespeichert, auch wenn ohne Pause
 * weitergetippt wird (PO 28.09.2026: „Notiz ist jetzt fortlaufend: deshalb
 * immer speichern und muss in Echtzeit bei den anderen Bearbeitern angezeigt
 * werden"). Ohne die Grenze sähen die Kollegen einen langen, in einem Zug
 * getippten Absatz erst nach der ersten Pause.
 */
const SPEICHER_SPAETESTENS_MS = 4000;

export type NotizFeldProps = {
    /** ALLE Notizen der Seite; das Feld filtert selbst auf seinen Strang. */
    notizen: BlockNotiz[];
    /** `null` = die Notiz des Vorgangs, sonst das To-do. */
    todoId: number | null;
    users: AppUser[];
    canEdit: boolean;
    aktuellerUserId: string;
    /**
     * Neuer Text aus der Schreibstelle. Liefert der Aufrufer den Schlüssel der
     * angelegten Zeile zurück, übernimmt das Feld ihn AN ORT UND STELLE —
     * ohne den Text kurz verschwinden zu lassen und ohne dem Schreibenden den
     * Cursor zu nehmen. Ohne Rückgabe wird die Schreibstelle geleert und der
     * Abschnitt kommt mit dem nächsten Laden.
     */
    onNeu: (
        html: string,
        refs?: NoteRefDto[],
        mentions?: string[],
    ) => Promise<{ key: string } | void>;
    onAendern: (block: Block, html: string, refs?: NoteRefDto[], mentions?: string[]) => Promise<void>;
    /** Leergeräumt heißt gelöscht (PO 13.09.2026). */
    onLoeschen: (block: Block) => Promise<void>;
    /**
     * Rückfrage vor dem Löschen MEHRERER oder FREMDER Abschnitte. Eine
     * gelöschte Notiz lässt sich in der Oberfläche nicht wiederherstellen (der
     * Papierkorb kennt nur ganze Vorgänge) — „alles markieren, Entf" nähme
     * sonst still die Notizen der Kollegen mit. Ohne die Angabe wird nicht
     * gefragt. Darf ein Promise liefern (eigenes Rückfrage-Popup).
     */
    bestaetigeLoeschen?: (anzahl: number, fremde: number) => boolean | Promise<boolean>;
    /** Das Angebot hinter dem `#`; fehlt es, bleibt `#` ein gewöhnliches Zeichen. */
    kandidaten?: BezugKandidat[];
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    onBildHochladen?: (file: File) => Promise<{ id: number }>;
    /**
     * Ein Notizbild vergrößert ansehen (User 22.10.2026: „Vergrößern müsste
     * man es auch können"). Im Text steht es klein (`.nf-feld img`). Im
     * schreibgeschützten Feld öffnet der Klick, im bearbeitbaren der
     * Doppelklick oder die Lupe der Bildleiste — der einfache Klick wählt dort
     * das Bild aus (Größe, Entfernen; User 23.10.2026). Ohne Angabe bleibt der
     * Klick ein Klick.
     */
    onBildOeffnen?: (bild: { id: number; name: string }) => void;
    /** Darf in DIESEM Abschnitt formatiert werden? Ohne Angabe: überall. */
    formatierbarFuer?: (block: Block) => boolean;
    /**
     * Der Kommentarfaden eines Abschnitts — die Sprechblase am Rand. Sie trägt
     * die Kommentare AUS DEM BESTAND, die an der ganzen Notiz hängen und keine
     * markierte Textstelle nennen; ohne sie wären genau die nicht mehr
     * erreichbar.
     */
    kommentareFuer?: (block: Block) => Kommentar[];
    onKommentarNeu?: (
        block: Block,
        text: string,
        mentions: string[],
        anker?: string,
        zusatz?: KommentarZusatz,
    ) => Promise<void>;
    onKommentarAendern?: KommentarAendern;
    onKommentarLoeschen?: (k: Kommentar) => Promise<void>;
    /**
     * Den Haken an einem Kommentar setzen (PO 28.09.2026: „Kommentare kann man
     * erledigen, war im Artifact auch so"). Bewusst NICHT an `canEdit`
     * gebunden: abhaken darf, wer den Vorgang sieht (PO 20.09.2026).
     */
    onKommentarErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
    /** Anhänge im Kommentar — nur, wenn `onKommentarNeu`/`…Aendern` den Zusatz auswerten. */
    kommentarMitAnhang?: boolean;
    /**
     * Die Fäden an markierten TEXTSTELLEN, geschlüsselt nach ihrem Anker
     * (`kommentareJeAnker`). Ohne die Angabe bleibt eine markierte Stelle
     * hinterlegt, zeigt aber nichts — dann liest jemand ohne die Notizen.
     */
    ankerFaeden?: Map<string, Kommentar[]>;
    /** Notiz-Schlüssel, die NICHT als Abschnitt stehen (sie sind Kommentare). */
    kommentarKeys?: Set<string>;
    /**
     * Zusätzliche Punkte im Rechtsklick-Menü eines Abschnitts — das
     * Kundentagebuch hängt dort „Einem Vorgang zuordnen", „Daraus einen
     * Vorgang machen" und „Löschen" an. Im Vorgang gibt es keine
     * (PO 20.09.2026: „Text kann man ja kopieren").
     */
    aktionenFuer?: (block: Block) => BlockAktion[];
    /**
     * Woher ein Abschnitt stammt, wenn nicht von hier (Kundentagebuch:
     * „über Ansprechpartner X"). Steht beim Überfahren hinter Name und Zeit.
     */
    herkunftFuer?: (block: Block) => string | null;
};

/** Text an der Einfügemarke einsetzen; die Marke steht danach dahinter. */
function fuegeTextEin(text: string): void {
    const sel = aktiveAuswahl();
    if (!sel || sel.rangeCount === 0) return;
    const r = sel.getRangeAt(0);
    const knoten = aktivesDokument().createTextNode(text);
    r.insertNode(knoten);
    r.setStartAfter(knoten);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
}

/** Eingaben, die eine Markierung ersetzen — sie löschen sie zuerst. */
const ERSETZT_AUSWAHL = new Set(["insertText", "insertReplacementText", "insertParagraph", "insertLineBreak"]);

/**
 * Welche Richtung ein Lösch-`inputType` hat — `deleteContentBackward`,
 * `deleteWordBackward`, `deleteSoftLineBackward` … zurück, die `…Forward`
 * vorwärts; alles andere (Ausschneiden, Ziehen) ist keine Tastengeste.
 */
export function loeschRichtung(inputType: string): "zurueck" | "vor" | null {
    if (!inputType.startsWith("delete") || inputType === "deleteByCut" || inputType === "deleteByDrag") return null;
    if (inputType.endsWith("Backward")) return "zurueck";
    if (inputType.endsWith("Forward")) return "vor";
    return null;
}

export const NotizFeld: React.FC<NotizFeldProps> = ({
    notizen,
    todoId,
    users,
    canEdit,
    aktuellerUserId,
    onNeu,
    onAendern,
    onLoeschen,
    bestaetigeLoeschen,
    kandidaten,
    onBezugOeffnen,
    onBildHochladen,
    onBildOeffnen,
    formatierbarFuer,
    kommentareFuer,
    onKommentarNeu,
    onKommentarAendern,
    onKommentarLoeschen,
    onKommentarErledigt,
    kommentarMitAnhang,
    ankerFaeden,
    kommentarKeys,
    aktionenFuer,
    herkunftFuer,
}) => {
    const huelleRef = useRef<HTMLDivElement | null>(null);
    const feldRef = useRef<HTMLDivElement | null>(null);
    // Rechtschreibung wie im Mail-Editor (LanguageTool, nur Highlights — der
    // Notiztext selbst bleibt unberührt). Erst ab dem ersten Fokus und erst
    // nach der ersten Änderung, damit alte Notizen beim Öffnen ruhig bleiben.
    const [rsFokus, setRsFokus] = useState(false);
    const rs = useRechtschreibung(feldRef, canEdit && rsFokus, { sofortPruefen: false });
    /** Cursor beim Start eines Diktats — dort landet der Text (wie getippt). */
    const diktatStelleRef = useRef<Range | null>(null);
    const diktatEinsetzen = (text: string) => {
        const el = feldRef.current;
        const gemerkt = diktatStelleRef.current;
        diktatStelleRef.current = null;
        if (!el) return;
        // Ohne gemerkte Stelle: an den Anfang der Schreibstelle (neue Notiz).
        inEditableEinfuegen(el, gemerkt, text, () => {
            const neu = el.querySelector(`[data-nk="${NEU}"]`);
            if (!neu) return null;
            const r = el.ownerDocument.createRange();
            r.selectNodeContents(neu);
            r.collapse(neu.textContent ? false : true);
            return r;
        });
    };
    /**
     * Das Feld, wie es beim Montieren gebaut wurde. Beim Abbauen ist
     * `feldRef` schon geleert, das letzte Speichern braucht den Text aber
     * noch — der Knoten selbst lebt weiter, bis niemand mehr auf ihn zeigt.
     */
    const gebautesFeldRef = useRef<HTMLDivElement | null>(null);
    /** Seit wann ungespeichert getippt wird — für {@link SPEICHER_SPAETESTENS_MS}. */
    const ungespeichertSeitRef = useRef<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Der zuletzt gespeicherte Stand je Abschnitt — der Vergleichspunkt. */
    const standRef = useRef<Map<string, string>>(new Map());
    /** Verweise und Erwähnungen der Schreibstelle. */
    const neuRefsRef = useRef<NoteRefDto[]>([]);
    const neuMentionsRef = useRef<string[]>([]);
    /**
     * Erwähnungen je gespeichertem Abschnitt, die der Verlauf womöglich noch
     * nicht kennt: in der Auswahl gewählt oder schon mitgeschickt. Ohne sie
     * schickte das nächste Speichern die alte Liste und striche sie wieder.
     */
    const mentionsJeAbschnittRef = useRef<Map<string, string[]>>(new Map());
    const blobUrlsRef = useRef<string[]>([]);
    const uploadsRef = useRef(0);
    const [token, setToken] = useState<AktivesToken | null>(null);
    /** Der per ↑/↓ markierte Vorschlag der offenen Auswahl (T-50). */
    const [aktiv, setAktiv] = useState(0);
    useEffect(() => setAktiv(0), [token?.ausloeser, token?.query]);
    const [menuePos, setMenuePos] = useState<{ x: number; y: number; imText: boolean } | null>(null);
    const [menueAktionen, setMenueAktionen] = useState<BlockAktion[]>([]);
    /**
     * Das ausgewählte Bild (User 23.10.2026) — Rahmen, Griff und Leiste
     * stehen dann darum (`BildLeiste`). Nur im bearbeitbaren Feld.
     */
    const [bildGewaehlt, setBildGewaehlt] = useState<HTMLImageElement | null>(null);
    /** Das Feld beim Greifen des Ziehgriffs — für EINEN Rückgängig-Schritt je Zug. */
    const zugVorherRef = useRef<string | null>(null);
    const menueOffenRef = useRef(false);
    /** Steht eine Markierung im Feld? Dann schwebt die Leiste darüber. */
    const [markiert, setMarkiert] = useState(false);
    /** Die Stelle, deren Faden gerade offen steht. */
    const [offeneStelle, setOffeneStelle] = useState<{ el: HTMLElement; fest: boolean } | null>(
        null,
    );
    /**
     * Der Anker, zu dem gerade ein erster Kommentar geschrieben wird — samt
     * der Stellen, die dafür schon hinterlegt sind (siehe `stelleKommentieren`).
     */
    const [neuerFaden, setNeuerFaden] = useState<{
        anker: string;
        block: Block;
        stellen: HTMLSpanElement[];
    } | null>(null);

    const sichtbare = useMemo(
        () =>
            kommentarKeys?.size
                ? notizen.filter((n) => !kommentarKeys.has(notizSchluessel(n)))
                : notizen,
        [notizen, kommentarKeys],
    );
    const bloecke = useMemo(() => bloeckeAus(sichtbare, todoId), [sichtbare, todoId]);
    const abschnitte = useMemo(() => abschnitteAus(bloecke), [bloecke]);
    /** Schlüssel → Block: `onAendern` und `onLoeschen` brauchen die ganze Zeile. */
    const blockVon = useMemo(() => new Map(bloecke.map((b) => [b.key, b])), [bloecke]);

    const nameVon = useCallback(
        (userId: string) => labelForUserKey(userId, users) || userId,
        [users],
    );

    /** Was am Rand steht: Kürzel, Farbe der Person, Name und Zeit beim Überfahren. */
    const marke = useCallback(
        (key: string) => {
            if (key === NEU) return null;
            const a = abschnitte.find((x) => x.key === key);
            if (!a) return null;
            const name = nameVon(a.autor);
            const block = herkunftFuer ? bloecke.find((b) => b.key === key) : undefined;
            const herkunft = block && herkunftFuer ? herkunftFuer(block) : null;
            return {
                kuerzel: kuerzelVon(findUserBy(users, a.autor), name),
                farbe: avatarColor(a.autor || name),
                titel: herkunft ? `${hoverTitel(a, nameVon)} · ${herkunft}` : hoverTitel(a, nameVon),
                // T-407: das Datum steht sichtbar über der Notiz, nicht nur im Hover.
                zeit: zeitpunkt(a.angelegt),
                gesperrt: a.gesperrt === true,
            };
        },
        // `users` MUSS hier stehen: Das Kürzel kommt jetzt aus dem
        // Benutzerverzeichnis, nicht mehr allein aus dem Namen. Wird die Liste
        // nachgeladen — sie kommt asynchron —, bliebe die Randmarke sonst auf
        // dem Namens-Fallback stehen, bis etwas anderes ein Neuzeichnen auslöst.
        [abschnitte, nameVon, users, bloecke, herkunftFuer],
    );
    const markeRef = useRef(marke);
    markeRef.current = marke;

    const eindampfen = useCallback(
        (html: string) => entwaesserBilder(entwaesserBezuege(html)),
        [],
    );

    /**
     * Die Sprechblasen stehen NEBEN dem Feld und müssen auf der Höhe ihres
     * Abschnitts sitzen. Gemessen statt gerechnet: Wie hoch ein Abschnitt ist,
     * weiß erst der Browser — er hängt an Schriftgröße, Zeilenumbrüchen,
     * Bildern und der Fensterbreite.
     *
     * Direkt am DOM statt über den State: Eine Positionsangabe im State löste
     * bei jedem Tastendruck ein Rendern aus, und das Feld ist bewusst
     * uncontrolled.
     */
    const markenStellen = useCallback(() => {
        const huelle = huelleRef.current;
        const feld = feldRef.current;
        if (!huelle || !feld) return;
        // Von Hand suchen statt per Selektor: `data-nk` trägt einen
        // Doppelpunkt (`vorgang:12`), der in einem Attributselektor maskiert
        // werden müsste — und `CSS.escape` gibt es nicht überall (in jsdom
        // etwa nicht).
        const kinder = Array.from(feld.children) as HTMLElement[];
        for (const m of Array.from(huelle.querySelectorAll<HTMLElement>(".nf-marke"))) {
            const key = m.getAttribute("data-nk");
            const ziel = key ? kinder.find((k) => k.getAttribute("data-nk") === key) : null;
            if (!ziel) {
                m.hidden = true;
                continue;
            }
            m.hidden = false;
            m.style.top = `${ziel.offsetTop}px`;
        }
    }, []);

    /** Karten und Bilder in einem frisch eingehängten Abschnitt sichtbar machen. */
    const aufblasen = useCallback(
        (kind: HTMLElement) => {
            hydratisiereBezuege(kind, kandidaten ?? [], []);
            void hydratisiereBilder(kind, { merke: (u) => blobUrlsRef.current.push(u) });
        },
        [kandidaten],
    );

    // ── EINMAL bauen. Absicht: keine Abhängigkeiten — von außen kommende
    //    Änderungen trägt der Abgleich unten ein, nicht ein Neuaufbau.
    useEffect(() => {
        const el = feldRef.current;
        if (!el) return;
        gebautesFeldRef.current = el;
        baueFeld(el, abschnitte);
        for (const kind of Array.from(el.children) as HTMLElement[]) {
            hydratisiereBezuege(kind, kandidaten ?? [], []);
        }
        normalisiere(el, markeRef.current);
        standRef.current = new Map(
            leseAbschnitte(el, eindampfen)
                .filter((s) => s.key !== NEU)
                .map((s) => [s.key, s.html]),
        );
        void hydratisiereBilder(el, { merke: (u) => blobUrlsRef.current.push(u) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Die Blasen nachführen: nach jedem Rendern, bei jeder Größenänderung des
    // Feldes (ein nachgeladenes Bild ändert die Höhe, ohne dass jemand tippt).
    useEffect(() => {
        markenStellen();
        const feld = feldRef.current;
        if (!feld || typeof ResizeObserver === "undefined") return;
        const beobachter = new ResizeObserver(() => markenStellen());
        beobachter.observe(feld);
        return () => beobachter.disconnect();
    });

    useEffect(
        () => () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            gibBilderFrei(blobUrlsRef.current);
        },
        [],
    );

    /**
     * Läuft gerade das Anlegen einer neuen Notiz? Dann wartet der Abgleich.
     *
     * HOTFIX 22.09.2026 („wenn ich eine notiz schreibe dann wird sie doppelt
     * gespeichert"): Der Aufrufer lädt nach dem Anlegen den Verlauf neu, BEVOR
     * er den Schlüssel zurückgibt. Der Abgleich fand die neue Zeile dann nicht
     * im Feld — der getippte Text trug ja noch `NEU` — und hängte sie ein
     * zweites Mal ein. Danach bekam der getippte Text denselben Schlüssel, und
     * das nächste Speichern las beide Kopien als EINEN Abschnitt: der Text
     * stand doppelt in der Datenbank.
     */
    const anlegenLaeuftRef = useRef(false);
    const abgleichNachholenRef = useRef(false);
    const abschnitteRef = useRef(abschnitte);
    abschnitteRef.current = abschnitte;

    // ── Was ein anderer Browser geschrieben hat, kommt hier an.
    const ersterLauf = useRef(true);
    useEffect(() => {
        const el = feldRef.current;
        if (!el) return;
        if (ersterLauf.current) {
            ersterLauf.current = false;
            return;
        }
        if (anlegenLaeuftRef.current) {
            abgleichNachholenRef.current = true;
            return;
        }
        gleicheAb(el, abschnitte, standRef.current, {
            eindampfen,
            aufblasen,
            marke: markeRef.current,
        });
    }, [abschnitte, eindampfen, aufblasen]);

    /**
     * Speichern. `beimVerlassen` unterscheidet die beiden Auslöser: der
     * Ruhe-Timer schreibt GEÄNDERTES und NEUEN Text, das Verlassen zusätzlich
     * die Löschungen.
     *
     * Warum die Löschungen warten: Wer einen Abschnitt ausräumt, um ihn neu zu
     * schreiben, und dabei kurz überlegt, hätte ihn nach 1,2 s sonst verloren.
     *
     * Neuer Text dagegen wird seit dem 22.09.2026 schon in der Tipp-Pause
     * angelegt (PO: „die notiz soll einfach gespeichert werden vom gefühl her
     * wie in word"). Die erste Pause legt die Notiz an, jede weitere ändert
     * DIESELBE — der Text wird an Ort und Stelle übernommen, der Cursor bleibt.
     */
    const speichereEinmal = useCallback(
        async (beimVerlassen = false) => {
            const el = feldRef.current ?? gebautesFeldRef.current;
            if (!el || !canEdit) return;
            ungespeichertSeitRef.current = null;
            // Mitten im Hochladen nicht speichern: der Platzhalter ist kein
            // Inhalt. Der Upload stößt das Speichern selbst wieder an.
            if (uploadsRef.current > 0) return;
            normalisiere(el, markeRef.current);
            const stand = leseAbschnitte(el, eindampfen);
            const { neu, geaendert, geloescht } = aenderungen(standRef.current, stand);

            for (const a of geaendert) {
                const block = blockVon.get(a.key);
                if (!block) continue;
                // Handschrift vom reMarkable: Der Browser kann ihr HTML anders
                // zurückgeben, geschrieben wird sie aber nur von der Abholung.
                if (block.gesperrt) continue;
                const vorher = standRef.current.get(a.key);
                standRef.current.set(a.key, a.html);
                try {
                    const refs = refsAus(el, a.key);
                    // T-406: Wer erst nach dem Anlegen erwähnt wird, kommt so
                    // noch in die Liste — der Server meldet die Neuen.
                    const mentions = vereinigeMentions(
                        block.mentions,
                        mentionsJeAbschnittRef.current.get(a.key),
                        neuErwaehnt(vorher, a.html, users),
                    );
                    mentionsJeAbschnittRef.current.set(a.key, mentions);
                    await (refs.length ? onAendern(block, a.html, refs, mentions) : onAendern(block, a.html, undefined, mentions));
                } catch (err) {
                    console.error("[NotizFeld] Speichern fehlgeschlagen", err);
                }
            }

            if (beimVerlassen && geloescht.length) {
                const bloeckeZuLoeschen = geloescht
                    .map((k) => blockVon.get(k))
                    .filter((b): b is Block => !!b);
                const fremde = bloeckeZuLoeschen.filter((b) => b.autor !== aktuellerUserId).length;
                // Eine einzelne Notiz geht ohne Rückfrage — auch eine fremde
                // (User 03.11.2026: „darf nicht sein, einfach löschen"). Gefragt
                // wird nur, wenn eine Auswahl MEHRERE Notizen mitnimmt.
                // Handschrift vom reMarkable verschwindet nie ohne Rückfrage: Sie
                // lässt sich hier nicht neu schreiben, und eine Auswahl über
                // mehrere Abschnitte nimmt den gesperrten Block im Browser mit.
                const fragen = bloeckeZuLoeschen.length > 1 || bloeckeZuLoeschen.some((b) => b.gesperrt);
                // Ohne Rückfrage-Popup bleibt ein gesperrter Block lieber stehen.
                const abgelehnt = bestaetigeLoeschen
                    ? fragen && !(await bestaetigeLoeschen(bloeckeZuLoeschen.length, fremde))
                    : bloeckeZuLoeschen.some((b) => b.gesperrt);
                if (abgelehnt) {
                    // Abgelehnt: die Notizen kommen sichtbar zurück. Ein
                    // geleerter Abschnitt steht noch als leere Zeile im Feld;
                    // die hält der Abgleich für eine ungespeicherte Eingabe
                    // und ließe sie leer (T-464: weg im Feld, aber nicht
                    // gelöscht — beim nächsten Laden wieder da). Ohne die
                    // Zeile setzt er den gespeicherten Text neu ein.
                    for (const block of bloeckeZuLoeschen) for (const k of kinderVon(el, block.key)) k.remove();
                    gleicheAb(el, abschnitte, standRef.current, {
                        eindampfen,
                        aufblasen,
                        marke: markeRef.current,
                    });
                    return;
                }
                for (const block of bloeckeZuLoeschen) {
                    standRef.current.delete(block.key);
                    try {
                        await onLoeschen(block);
                        // Gelöscht heißt weg — auch die leere Zeile mit dem
                        // Kürzel am Rand (T-464: „dann auch den Eintrag
                        // entfernen"). Der Abgleich ließe sie stehen: Für ihn
                        // ist eine leere Zeile ohne Stand eine Eingabe.
                        for (const k of kinderVon(el, block.key)) {
                            if (istLeererBlocktext(k.innerHTML)) k.remove();
                        }
                    } catch (err) {
                        console.error("[NotizFeld] Löschen fehlgeschlagen", err);
                    }
                }
            }

            if (neu && !istLeer(neu)) {
                const neuKinder = () =>
                    (Array.from(el.children) as HTMLElement[]).filter(
                        (k) => (k.getAttribute("data-nk") ?? NEU) === NEU,
                    );
                const kinder = neuKinder();
                const refs = neuRefsRef.current;
                const mentions = vereinigeMentions(neuMentionsRef.current, erwaehnteIn(neu, users));
                anlegenLaeuftRef.current = true;
                try {
                    const ergebnis = await (mentions.length
                        ? onNeu(neu, refs, mentions)
                        : refs.length
                          ? onNeu(neu, refs)
                          : onNeu(neu));
                    neuRefsRef.current = [];
                    neuMentionsRef.current = [];
                    if (ergebnis?.key) {
                        if (mentions.length) mentionsJeAbschnittRef.current.set(ergebnis.key, mentions);
                        // AN ORT UND STELLE übernehmen: Der Text bleibt stehen,
                        // bekommt nur seinen Schlüssel und das Zeichen am Rand.
                        // ALLE Zeilen der Schreibstelle, nicht nur die beim
                        // Absenden gezählten: Wer während des Speicherns
                        // weitertippt oder Enter drückt, schreibt in dieselbe
                        // Notiz — das nächste Speichern ändert sie dann.
                        for (const k of neuKinder()) k.setAttribute("data-nk", ergebnis.key);
                        standRef.current.set(ergebnis.key, neu);
                        el.insertBefore(
                            (() => {
                                const s = document.createElement("div");
                                s.setAttribute("data-nk", NEU);
                                s.appendChild(document.createElement("br"));
                                return s;
                            })(),
                            el.firstChild,
                        );
                    } else {
                        for (const k of kinder) k.remove();
                    }
                    normalisiere(el, markeRef.current);
                } catch (err) {
                    console.error("[NotizFeld] Neue Notiz speichern fehlgeschlagen", err);
                } finally {
                    anlegenLaeuftRef.current = false;
                    // Den übergangenen Abgleich nachholen — jetzt trägt der
                    // getippte Text seinen Schlüssel und wird wiedererkannt.
                    if (abgleichNachholenRef.current) {
                        abgleichNachholenRef.current = false;
                        gleicheAb(el, abschnitteRef.current, standRef.current, {
                            eindampfen,
                            aufblasen,
                            marke: markeRef.current,
                        });
                    }
                }
            }
        },
        [
            canEdit,
            eindampfen,
            blockVon,
            onAendern,
            onLoeschen,
            onNeu,
            aktuellerUserId,
            bestaetigeLoeschen,
            abschnitte,
            aufblasen,
            users,
        ],
    );

    /**
     * Immer nur EIN Speichern zur Zeit.
     *
     * Zwei gleichzeitige Läufe (Feld verlassen + Formatiermenü schließen, oder
     * Ruhe-Timer + Verlassen) sahen beide denselben neuen Text und legten ihn
     * beide an. Wer kommt, während einer läuft, wird vorgemerkt und danach EIN
     * Mal nachgeholt — mit dem Stand von dann; ein „beim Verlassen" bleibt
     * dabei erhalten.
     */
    const speichereEinmalRef = useRef(speichereEinmal);
    speichereEinmalRef.current = speichereEinmal;
    const laufRef = useRef<Promise<void> | null>(null);
    const vorgemerktRef = useRef<{ beimVerlassen: boolean } | null>(null);
    const speichern = useCallback((beimVerlassen = false): Promise<void> => {
        if (laufRef.current) {
            vorgemerktRef.current = {
                beimVerlassen: beimVerlassen || !!vorgemerktRef.current?.beimVerlassen,
            };
            return laufRef.current;
        }
        const lauf = (async () => {
            try {
                await speichereEinmalRef.current(beimVerlassen);
                while (vorgemerktRef.current) {
                    const nochmal = vorgemerktRef.current;
                    vorgemerktRef.current = null;
                    await speichereEinmalRef.current(nochmal.beimVerlassen);
                }
            } finally {
                laufRef.current = null;
            }
        })();
        laufRef.current = lauf;
        return lauf;
    }, []);

    const angestossen = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        const jetzt = Date.now();
        if (ungespeichertSeitRef.current === null) ungespeichertSeitRef.current = jetzt;
        const rest = SPEICHER_SPAETESTENS_MS - (jetzt - ungespeichertSeitRef.current);
        timerRef.current = setTimeout(
            () => {
                timerRef.current = null;
                void speichern();
            },
            Math.max(0, Math.min(SPEICHER_RUHE_MS, rest)),
        );
    };

    /*
     * IMMER SPEICHERN — auch beim Gehen (PO 28.09.2026). Wer mitten im Satz
     * die Seite wechselt, das Fenster schließt oder den Tab verlässt, verlöre
     * sonst, was seit der letzten Pause getippt wurde. Beim Abbauen des
     * Feldes, beim Verstecken der Seite und bei `pagehide` wird deshalb
     * sofort gespeichert. Löschungen bleiben beim Verlassen des FELDES (sie
     * brauchen womöglich eine Rückfrage, und die gehört nicht in ein
     * schließendes Fenster).
     */
    useEffect(() => {
        const sofort = () => {
            if (timerRef.current === null && ungespeichertSeitRef.current === null) return;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = null;
            void speichern();
        };
        const versteckt = () => {
            if (document.visibilityState === "hidden") sofort();
        };
        document.addEventListener("visibilitychange", versteckt);
        window.addEventListener("pagehide", sofort);
        return () => {
            document.removeEventListener("visibilitychange", versteckt);
            window.removeEventListener("pagehide", sofort);
            sofort();
        };
    }, [speichern]);

    /*
     * Rücktaste am Zeilenanfang bzw. Entf am Zeilenende: die Sperre der
     * Erwähnungen kurz aufheben, damit Safari die zwei Zeilen zusammenführt,
     * statt die untere zu verschlucken (`entsperreAnZeilengrenze`, T-384).
     * Über `beforeinput` statt `keydown` — so zählt auch die Rücktaste der
     * iPad-Bildschirmtastatur, egal welches Tastenereignis sie meldet.
     */
    useEffect(() => {
        const el = feldRef.current;
        if (!el) return;
        const vorEingabe = (e: Event) => {
            const art = (e as InputEvent).inputType ?? "";
            /*
             * EINE MARKIERUNG ÜBER MEHRERE NOTIZEN (T-464): Löschen oder
             * Überschreiben übernimmt das Feld selbst — der Browser zöge die
             * nächste Notiz in die markierte hinein (Dreifachklick + Entf
             * speicherte so den Text der Kollegin unter dem eigenen Namen).
             */
            if (
                (art.startsWith("delete") || ERSETZT_AUSWAHL.has(art)) &&
                loescheAuswahlUeberAbschnitte(el)
            ) {
                e.preventDefault();
                // Getippte Zeichen selbst an die Marke setzen — `execCommand`
                // aus diesem Ereignis heraus schrieb im Browser in die NÄCHSTE
                // Notiz. Ein Zeilenumbruch beim Überschreiben bleibt aus.
                const daten = (e as InputEvent).data;
                if ((art === "insertText" || art === "insertReplacementText") && daten) fuegeTextEin(daten);
                // Wie nach jeder Eingabe: Ränder neu setzen, Speichern anstoßen.
                el.dispatchEvent(new Event("input", { bubbles: true }));
                return;
            }
            const richtung = loeschRichtung(art);
            if (!richtung) return;
            /*
             * DIE ABSCHNITTSGRENZE AUCH HIER (Plan „Vorgangsdetail mobil",
             * PR 5 — Pflicht, seit Notizen mobil direkt bearbeitet werden,
             * Entscheidung 4 des Users). Android-Bildschirmtastaturen melden
             * im `keydown` oft `key: "Unidentified"` (keyCode 229) — die
             * Sperre dort griff nicht, und die Rücktaste am Anfang eines
             * Abschnitts verschmolz eine fremde Notiz still mit der eigenen.
             * `beforeinput` trägt die Absicht unabhängig von der Taste; auch
             * „Wort löschen" (langes Drücken) zählt. Hält der Browser das
             * Ereignis für nicht abbrechbar (mitten in einer Komposition),
             * bleibt nur der Hinweis im Code — mobil gibt es dafür das
             * Notiz-Sheet für Neues.
             */
            if (anAbschnittsGrenze(el, richtung)) {
                e.preventDefault();
                return;
            }
            if (art !== "deleteContentBackward" && art !== "deleteContentForward") return;
            if (!entsperreAnZeilengrenze(el, richtung)) return;
            // Kommt danach kein `input` (nichts zu löschen), sperrt das hier.
            window.setTimeout(() => sperreEingesetztes(el), 0);
        };
        el.addEventListener("beforeinput", vorEingabe);
        return () => el.removeEventListener("beforeinput", vorEingabe);
    }, []);

    /** Die Verweise, die IN diesem Abschnitt stehen — aus dem DOM, nicht mitgeführt. */
    const refsAus = (feld: HTMLElement, key: string): NoteRefDto[] => {
        const out: NoteRefDto[] = [];
        for (const kind of Array.from(feld.children) as HTMLElement[]) {
            if ((kind.getAttribute("data-nk") ?? NEU) !== key) continue;
            for (const chip of Array.from(kind.querySelectorAll("[data-ref-art][data-ref-id]"))) {
                const art = chip.getAttribute("data-ref-art") as NoteRefDto["art"] | null;
                const id = chip.getAttribute("data-ref-id");
                if (art && id) out.push({ art, id, label: chip.textContent ?? null });
            }
        }
        return vereinigeRefs(out);
    };

    /* ── Markieren: Kommentar oder Erledigt (PO 18.09.2026) ──────────────── */

    /**
     * Steht eine Markierung IM Feld? Die Leiste darf nicht über einer Auswahl
     * irgendwo sonst auf der Seite erscheinen — und nicht über einer, die nur
     * aus dem Wandern des Cursors besteht.
     */
    const markierungPruefen = useCallback(() => {
        const el = feldRef.current;
        const sel = typeof window !== "undefined" ? window.getSelection?.() : null;
        if (!el || !canEdit || !sel || sel.isCollapsed || sel.rangeCount === 0) {
            setMarkiert(false);
            return;
        }
        const r = sel.getRangeAt(0);
        const drin =
            el.contains(r.startContainer) &&
            el.contains(r.endContainer) &&
            (r.toString() ?? "").trim() !== "";
        letzteMarkierungRef.current = drin ? r.cloneRange() : null;
        setMarkiert(drin);
    }, [canEdit]);

    /*
     * DIE MARKIERUNG MERKEN (Plan „Vorgangsdetail mobil", PR 6 —
     * Entscheidung 6: markierte Stellen auch mobil kommentieren). Auf dem
     * Handy räumt das Antippen der Leiste die Auswahl oft weg, bevor der Klick
     * ankommt (iOS/Android schließen dabei ihr eigenes Auswahlmenü). Dann gilt
     * die Markierung, die beim Erscheinen der Leiste stand — solange ihre
     * Knoten noch im Feld hängen.
     */
    const letzteMarkierungRef = useRef<Range | null>(null);
    const markierungsRange = (): Range | null => {
        const el = feldRef.current;
        if (!el) return null;
        const sel = aktiveAuswahl();
        if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
            const r = sel.getRangeAt(0);
            if (el.contains(r.startContainer) && el.contains(r.endContainer)) return r;
        }
        const gemerkt = letzteMarkierungRef.current;
        if (
            gemerkt &&
            !gemerkt.collapsed &&
            gemerkt.startContainer.isConnected &&
            el.contains(gemerkt.startContainer) &&
            el.contains(gemerkt.endContainer)
        ) {
            return gemerkt;
        }
        return null;
    };
    /** Der Abschnitt (`data-nk`) eines Range — auch, wenn die Auswahl schon weg ist. */
    const keyVonRange = (el: HTMLElement, r: Range): string | null => {
        let n: Node | null = r.startContainer;
        while (n && n.parentNode !== el) n = n.parentNode;
        if (!n || !(n as HTMLElement).getAttribute) return null;
        return (n as HTMLElement).getAttribute("data-nk") ?? null;
    };

    // Erst zeigen, wenn die Markierung FERTIG ist (PO 28.09.2026) — nicht
    // schon beim Ziehen. Die Regel steht in `markierungFertig.ts`.
    useMarkierungFertig(feldRef, markierungPruefen, () => setMarkiert(false));

    /** Der Abschnitt, in dem die Markierung steht — sein Block wird gespeichert. */
    const blockDerMarkierung = (): Block | null => {
        const el = feldRef.current;
        if (!el) return null;
        const r = markierungsRange();
        const key = (r ? keyVonRange(el, r) : null) ?? fokussierterKey(el);
        return key && key !== NEU ? (blockVon.get(key) ?? null) : null;
    };

    /**
     * ERLEDIGT: die Stelle durchgestrichen, Haken dahinter (PO 18.09.2026:
     * „also durchgestrichen und danach ein Häkchen"). Sie bleibt stehen
     * (PO 20.09.2026: „ja durchgestrichen") — erledigt heißt getan, nicht weg.
     */
    const alsErledigtMarkieren = () => {
        const el = feldRef.current;
        const r = markierungsRange();
        if (!el || !r) return;
        umwickle(r, KLASSE_GETAN, {
            "data-von": aktuellerUserId,
            "data-am": new Date().toISOString(),
        });
        letzteMarkierungRef.current = null;
        aktiveAuswahl()?.removeAllRanges();
        setMarkiert(false);
        normalisiere(el, markeRef.current);
        void speichern();
    };

    /**
     * KOMMENTAR: die Stelle SOFORT hinterlegen, den Faden beim Absenden anlegen.
     *
     * Bis zum 28.09.2026 wurde erst NACH dem Anlegen des Kommentars umwickelt
     * — an einem beim Klick festgehaltenen `Range`. Auf DEV standen danach
     * Kommentare mit Anker, deren Stelle im Text fehlte: Das Anlegen lädt den
     * Verlauf neu, der Abgleich darf einen unberührten Abschnitt dabei neu
     * aufbauen, und der festgehaltene `Range` zeigte dann auf Knoten, die
     * nicht mehr im Feld hängen. Umwickelt wurde ins Leere.
     *
     * Jetzt ist der Abschnitt schon beim Klick verändert: der Abgleich lässt
     * ihn in Ruhe (`gleicheAb`: „hier geändert, noch nicht gespeichert"), und
     * die Stelle bleibt beim Schreiben sichtbar hinterlegt. Wer abbricht, dem
     * wird sie wieder ausgewickelt (`kommentarAbbrechen`).
     */
    const stelleKommentieren = () => {
        const el = feldRef.current;
        const r = markierungsRange();
        const block = blockDerMarkierung();
        if (!el || !r || !block || !onKommentarNeu) return;
        const anker = neuerAnker();
        const stellen = umwickle(r, KLASSE_ANMERK, { "data-anker": anker });
        if (!stellen.length) return;
        letzteMarkierungRef.current = null;
        aktiveAuswahl()?.removeAllRanges();
        setMarkiert(false);
        normalisiere(el, markeRef.current);
        setNeuerFaden({ anker, block, stellen });
    };

    /** Abgebrochen: die hinterlegte Stelle wieder auswickeln — nichts bleibt zurück. */
    const kommentarAbbrechen = () => {
        const el = feldRef.current;
        for (const s of neuerFaden?.stellen ?? []) if (s.isConnected) wickleAus(s);
        setNeuerFaden(null);
        if (el) {
            normalisiere(el, markeRef.current);
            angestossen();
        }
    };

    /** Den ersten Kommentar zur markierten Stelle abgeben. */
    const ersterKommentar = async (text: string, mentions: string[], zusatz?: KommentarZusatz) => {
        const el = feldRef.current;
        if (!el || !neuerFaden || !onKommentarNeu) return;
        // Scheitert das Anlegen, wirft es bis ins Schreibfeld durch — dort
        // steht die Meldung, und die Stelle bleibt für den nächsten Versuch.
        await (zusatz
            ? onKommentarNeu(neuerFaden.block, text, mentions, neuerFaden.anker, zusatz)
            : onKommentarNeu(neuerFaden.block, text, mentions, neuerFaden.anker));
        setNeuerFaden(null);
        normalisiere(el, markeRef.current);
        await speichern();
    };

    /**
     * Klick auf eine markierte Stelle. Ein Haken wird zurückgenommen, ein
     * Kommentar aufgeschlagen — beides an derselben Stelle, beides ein Klick.
     */
    const stelleGeklickt = (ziel: EventTarget | null): boolean => {
        const stelle = stelleUnter(ziel);
        if (!stelle) return false;
        if (stelle.classList.contains(KLASSE_GETAN)) {
            if (!canEdit) return true;
            wickleAus(stelle);
            const el = feldRef.current;
            if (el) normalisiere(el, markeRef.current);
            void speichern();
            return true;
        }
        setOffeneStelle({ el: stelle, fest: true });
        return true;
    };

    /* ── Überfahren einer kommentierten Stelle ──────────────────────────────
       Beim Verlassen der Stelle schließt der nur überfahrene Faden mit kurzer
       Verzögerung: Er steht seit dem 28.09.2026 NEBEN der Stelle, und auf
       dem Weg dorthin verlässt die Maus zwangsläufig die Stelle. Wer im
       Faden ankommt, hält ihn fest (`anmerkFesthalten`). */
    const hoverZuRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const anmerkUeber = (stelle: HTMLElement | null) => {
        if (!stelle || !stelle.classList.contains(KLASSE_ANMERK)) return;
        if (hoverZuRef.current) clearTimeout(hoverZuRef.current);
        // Ein festgehaltener Faden bleibt, wo er ist — sonst spränge er beim
        // Vorbeifahren an eine andere Stelle.
        setOffeneStelle((v) => (v?.fest ? v : { el: stelle, fest: false }));
    };
    const anmerkRaus = (stelle: HTMLElement | null) => {
        if (!stelle) return;
        if (hoverZuRef.current) clearTimeout(hoverZuRef.current);
        hoverZuRef.current = setTimeout(() => {
            setOffeneStelle((v) => (v && !v.fest && v.el === stelle ? null : v));
        }, 300);
    };
    const anmerkFesthalten = () => {
        if (hoverZuRef.current) clearTimeout(hoverZuRef.current);
        setOffeneStelle((v) => (v ? { ...v, fest: true } : v));
    };
    useEffect(() => () => {
        if (hoverZuRef.current) clearTimeout(hoverZuRef.current);
    }, []);
    const erledigteCss = useMemo(() => erledigteStellenCss(ankerFaeden), [ankerFaeden]);

    const darfFormatieren = (key: string): boolean => {
        if (key === NEU) return true;
        const block = blockVon.get(key);
        return !block || (formatierbarFuer?.(block) ?? true);
    };

    /** Gewählten Bezug einsetzen — die Verweisliste liest `refsAus` später aus dem DOM. */
    const waehlen = (k: BezugKandidat) => {
        setToken(null);
        const el = feldRef.current;
        if (!bezugEinsetzen(aktiveAuswahl(), k)) return;
        if (fokussierterKey(el!) === NEU) {
            neuRefsRef.current = vereinigeRefs(neuRefsRef.current, [
                { art: k.art, id: k.id, label: k.titel },
            ]);
        }
        if (el) hydratisiereBezuege(el, kandidaten ?? [], []);
        angestossen();
    };

    const erwaehnen = (u: AppUser) => {
        setToken(null);
        const el = feldRef.current;
        if (!mentionEinsetzen(aktiveAuswahl(), mentionToken(u))) return;
        const key = el ? fokussierterKey(el) : null;
        if (key === NEU) {
            neuMentionsRef.current = vereinigeMentions(neuMentionsRef.current, [u.id]);
        } else if (key) {
            // Die Zeile ist schon gespeichert — etwa, weil die Tipp-Pause beim
            // Warten auf die Auswahl die Notiz angelegt hat (T-406).
            const bisher = mentionsJeAbschnittRef.current.get(key);
            mentionsJeAbschnittRef.current.set(key, vereinigeMentions(bisher, [u.id]));
        }
        angestossen();
    };

    const medien = canEdit && !!onBildHochladen;

    const bildEinfuegen = async (file: File) => {
        const el = feldRef.current;
        if (!el || !onBildHochladen) return;
        await bildHochladenUndEinsetzen(el, file, onBildHochladen, {
            merke: (u) => blobUrlsRef.current.push(u),
            laufend: uploadsRef,
        });
        angestossen();
    };

    const einfuegen = (e: React.ClipboardEvent) => {
        const el = feldRef.current;
        if (!el) return;
        const dateien = medien ? einfuegeBildDateien(e.clipboardData) : [];
        if (dateien.length) {
            e.preventDefault();
            for (const f of dateien) void bildEinfuegen(f);
            return;
        }
        const url = urlAusText(e.clipboardData?.getData("text/plain"));
        if (url) {
            e.preventDefault();
            mitVerlauf(el, () => linkEinsetzen(el, aktiveAuswahl(), url));
            angestossen();
            return;
        }
        // Text mit Auszeichnung (Outlook, Word, Web): sofort durch den
        // Reiniger, nicht erst beim Speichern — sonst überdeckte sein
        // Inline-`style` die Palette bis zum Neuladen (T-282). Reiner Text
        // bleibt beim Browser.
        const html = e.clipboardData?.getData("text/html");
        if (!html) return;
        e.preventDefault();
        // Bilder, die der Reiniger verwirft (eingebettet, aus Outlook/Word,
        // Screenshot-Werkzeuge), werden hochgeladen und hinter den Text
        // gesetzt. Die Zwischenablage gilt nur während des Ereignisses —
        // deshalb jetzt einsammeln.
        const verworfen = medien ? verworfeneBildQuellen(html) : [];
        const ablageDateien = verworfen.length ? bildDateienAus(e.clipboardData) : [];
        const sauber = bereinigeEingefuegtesHtml(html, reinigeBlockHtml);
        mitVerlauf(el, () => {
            if (!htmlEinsetzenAnAuswahl(el, aktiveAuswahl(), sauber)) return;
            normalisiere(el, markeRef.current);
        });
        // Mitkopierte eigene Notizbilder tragen nach dem Reiniger nur noch
        // ihren Marker — sichtbar macht sie erst das Nachladen.
        void hydratisiereBilder(el, { merke: (u) => blobUrlsRef.current.push(u) });
        markenStellen();
        angestossen();
        if (verworfen.length) {
            void (async () => {
                for (const f of await bilderAusQuellen(verworfen, ablageDateien)) await bildEinfuegen(f);
            })();
        }
    };

    /** Das Bild unter einem Klick — nur echte Bilder im Feld, kein Upload-Platzhalter. */
    const bildUnter = (ziel: EventTarget | null): HTMLImageElement | null => {
        const el = feldRef.current;
        const img = (ziel as HTMLElement | null)?.closest?.("img") as HTMLImageElement | null;
        return img && el?.contains(img) ? img : null;
    };

    /** Ein Notizbild im Viewer öffnen. `false`, wenn es keins ist oder niemand öffnet. */
    const oeffneBild = (img: HTMLImageElement): boolean => {
        if (!onBildOeffnen) return false;
        const id = Number(img.getAttribute("data-bild-id"));
        if (!Number.isFinite(id) || id <= 0) return false;
        onBildOeffnen({ id, name: img.getAttribute("alt") || "Bild" });
        return true;
    };

    /*
     * ── Bild bearbeiten (User 23.10.2026) ──────────────────────────────────
     * Jede Änderung läuft über den Rückgängig-Verlauf des Feldes
     * (`formatVerlauf.ts`) — Strg+Z nimmt sie zurück wie jeden Palettenschritt
     * — und stößt das Speichern an. Die Breite steht danach als `width` am
     * Marker und überlebt Reiniger und Entwässern (`bildBreite.ts`).
     */
    const bildBreiteMax = () => {
        const el = feldRef.current;
        return el ? feldInnenBreite(el) : 2000;
    };
    const bildStufe = (richtung: 1 | -1) => {
        const el = feldRef.current;
        const img = bildGewaehlt;
        if (!el || !img?.isConnected) return;
        mitVerlauf(el, () => stufeBildBreite(img, richtung, bildBreiteMax()));
        angestossen();
    };
    const bildEntfernen = () => {
        const el = feldRef.current;
        const img = bildGewaehlt;
        setBildGewaehlt(null);
        if (!el || !img?.isConnected) return;
        el.focus();
        mitVerlauf(el, () => {
            entferneBild(img, aktiveAuswahl());
            normalisiere(el, markeRef.current);
        });
        markenStellen();
        angestossen();
    };
    const bildZiehBeginn = () => {
        zugVorherRef.current = feldRef.current?.innerHTML ?? null;
    };
    const bildZiehen = (breite: number) => {
        const img = bildGewaehlt;
        if (!img?.isConnected) return;
        setzeBildBreite(img, breite, bildBreiteMax());
    };
    const bildZiehEnde = () => {
        const el = feldRef.current;
        const vorher = zugVorherRef.current;
        zugVorherRef.current = null;
        if (!el || vorher === null || vorher === el.innerHTML) return;
        merkeSchritt(el, vorher);
        markenStellen();
        angestossen();
    };

    const bezugKlick = (e: React.MouseEvent) => {
        // Eine markierte Stelle fängt den Klick ab: Haken zurücknehmen oder
        // den Faden aufschlagen. Sonst setzte der Klick nur den Cursor.
        if (stelleGeklickt(e.target)) {
            e.preventDefault();
            return;
        }
        const bildImFeld = bildUnter(e.target);
        if (bildImFeld && canEdit) {
            // Bearbeitbar: der Klick WÄHLT das Bild (Größe, Entfernen);
            // groß öffnen Doppelklick und Lupe.
            setBildGewaehlt(bildImFeld);
            return;
        }
        setBildGewaehlt(null);
        if (bildImFeld && oeffneBild(bildImFeld)) {
            e.preventDefault();
            return;
        }
        const href = linkUnter(e.target);
        if (href) {
            e.preventDefault();
            window.open(href, "_blank", "noopener,noreferrer");
            return;
        }
        if (!onBezugOeffnen) return;
        const ziel = (e.target as HTMLElement | null)?.closest?.("[data-ref-art]");
        if (!ziel) return;
        const art = ziel.getAttribute("data-ref-art") as NoteRefArt | null;
        const id = ziel.getAttribute("data-ref-id");
        if (!art || !id) return;
        e.preventDefault();
        onBezugOeffnen(art, id);
    };

    /**
     * Rechtsklick: im Text die Formatierungsleiste, sonst das Menü des
     * Browsers. Block-Aktionen gibt es nicht mehr — „einem anderen To-do
     * zuweisen" ist am 20.09.2026 entfallen („nein, Text kann man ja
     * kopieren").
     */
    const menueOeffnen = (e: React.MouseEvent) => {
        const el = feldRef.current;
        const imText = !!el?.contains(e.target as Node);
        const key = el ? fokussierterKey(el) : null;
        if (!imText || !canEdit || !darfFormatieren(key ?? NEU)) return;
        e.preventDefault();
        e.stopPropagation();
        menueOffenRef.current = true;
        // Die Aktionen gehören zu dem Abschnitt UNTER DER MAUS — nicht zu dem,
        // in dem der Cursor gerade steht.
        let kind: Node | null = e.target as Node;
        while (kind && el && kind.parentNode !== el) kind = kind.parentNode;
        const unter = (kind as HTMLElement | null)?.getAttribute?.("data-nk") ?? null;
        const block = unter && unter !== NEU ? blockVon.get(unter) : undefined;
        setMenueAktionen(block && aktionenFuer ? aktionenFuer(block) : []);
        setMenuePos({ x: e.clientX, y: e.clientY, imText });
    };

    return (
        <div className="nf-huelle" ref={huelleRef}>
            {/* Ganz abgehakte Stellen werden grün — als Stilregel, nicht als
                Klasse im gespeicherten Text (`erledigteStellenCss`). */}
            {erledigteCss ? <style>{erledigteCss}</style> : null}
            {rs.popup}
            {canEdit ? (
                <DiktatKnopf
                    className="dk-am-notizrand"
                    hinweisAusrichtung="rechts"
                    onStart={() => {
                        diktatStelleRef.current = auswahlIn(feldRef.current);
                    }}
                    onText={diktatEinsetzen}
                />
            ) : null}
            <div
                ref={feldRef}
                className="nf-feld"
                contentEditable={canEdit}
                suppressContentEditableWarning
                // Mit eigener Prüfung die des Browsers aus — sonst doppelte Linien.
                spellCheck={!rs.aktiv}
                lang="de"
                onFocus={() => setRsFokus(true)}
                role="textbox"
                aria-multiline="true"
                aria-label="Notizen"
                onClick={bezugKlick}
                onDoubleClick={(e: React.MouseEvent) => {
                    const img = bildUnter(e.target);
                    if (img && oeffneBild(img)) e.preventDefault();
                }}
                onContextMenu={menueOeffnen}
                onMouseOver={(e: React.MouseEvent) => anmerkUeber(stelleUnter(e.target))}
                onMouseOut={(e: React.MouseEvent) => anmerkRaus(stelleUnter(e.target))}
                {...(canEdit ? { onPaste: einfuegen } : {})}
                {...(medien
                    ? {
                          // Die Karte darum (`DateiDropZoneV4`) erkennt daran,
                          // dass ein gezogenes BILD hier landet — und zeigt dann
                          // nicht ihr „Dateien an … hochladen" (`eigeneAblage.ts`).
                          "data-eigene-ablage": "bilder",
                          onDrop: (e: React.DragEvent) => {
                              const dateien = bildDateienAus(e.dataTransfer);
                              if (!dateien.length) return;
                              e.preventDefault();
                              const el = feldRef.current;
                              if (el) auswahlAnPunkt(el, e.clientX, e.clientY);
                              for (const f of dateien) void bildEinfuegen(f);
                          },
                          onDragOver: (e: React.DragEvent) => {
                              if (traegtDateien(e.dataTransfer)) e.preventDefault();
                          },
                      }
                    : {})}
                onInput={() => {
                    const el = feldRef.current;
                    if (el) normalisiere(el, markeRef.current);
                    markenStellen();
                    angestossen();
                    setToken(
                        aktivesToken(aktiveAuswahl(), {
                            hash: !!kandidaten?.length,
                            at: !!users.length,
                        }),
                    );
                }}
                onKeyDown={(e) => {
                    // Ein ausgewähltes Bild: Entf/Rücktaste nehmen es heraus,
                    // Esc wählt ab; jede andere Taste wählt ab und tut, was sie
                    // sonst tut (Strg+Z holt ein entferntes Bild zurück).
                    if (bildGewaehlt) {
                        if ((e.key === "Delete" || e.key === "Backspace") && !e.ctrlKey && !e.metaKey && !e.altKey) {
                            e.preventDefault();
                            bildEntfernen();
                            return;
                        }
                        if (e.key === "Escape") {
                            e.preventDefault();
                            setBildGewaehlt(null);
                            return;
                        }
                        if (!["Shift", "Control", "Meta", "Alt"].includes(e.key)) setBildGewaehlt(null);
                    }
                    if (e.key === "Escape" && token !== null) {
                        e.preventDefault();
                        setToken(null);
                        return;
                    }
                    // Offene `@`-/`#`-Auswahl: ↑/↓ wechseln, Enter/Tab
                    // übernehmen den markierten Vorschlag (T-50).
                    if (
                        behandleAuswahlTastatur(e, token, { users, kandidaten }, aktiv, {
                            setAktiv,
                            erwaehnen,
                            waehlen,
                        })
                    )
                        return;
                    const el = feldRef.current;
                    // Strg+Z nimmt zuerst die Schritte der Palette zurück,
                    // die der Browser nicht kennt (`formatVerlauf.ts`, T-282);
                    // alles andere bleibt seine eingebaute Rückgängig-Funktion.
                    if (
                        el &&
                        (e.ctrlKey || e.metaKey) &&
                        !e.shiftKey &&
                        !e.altKey &&
                        e.key.toLowerCase() === "z" &&
                        rueckgaengig(el)
                    ) {
                        e.preventDefault();
                        normalisiere(el, markeRef.current);
                        markenStellen();
                        angestossen();
                        return;
                    }
                    // LISTEN ZUERST. Die Rücktaste im ersten Punkt einer Liste
                    // hebt die Aufzählung auf (`behandleListenTaste`) — sie
                    // verschmilzt nichts, der Punkt wird an Ort und Stelle ein
                    // Absatz. Bis zum 28.09.2026 kam die Sperre unten zuerst:
                    // Stand die Liste am Anfang eines Abschnitts, galt die
                    // Rücktaste als „Übertritt in die Notiz darüber" und tat
                    // gar nichts (PO: „1. konnte ich nicht löschen, musste die
                    // Aufzählung über Menü aufheben").
                    if (token === null && el && darfFormatieren(fokussierterKey(el) ?? NEU)) {
                        const erledigt = mitSchluesselErhalten(el, () =>
                            behandleListenTaste(el, e, angestossen),
                        );
                        if (erledigt) {
                            normalisiere(el, markeRef.current);
                            markenStellen();
                            return;
                        }
                    }
                    // DIE EINE GESPERRTE GESTE: Rücktaste am Anfang bzw. Entf
                    // am Ende eines Abschnitts. Der Browser verschmölze ihn
                    // sonst mit dem Nachbarn, und der Text einer Kollegin
                    // würde still ein Stück MEINER Notiz — ihre Zeile
                    // verschwände aus der Akte, ohne dass jemand gelöscht
                    // hätte. Begründung in `anAbschnittsGrenze`.
                    if (
                        el &&
                        ((e.key === "Backspace" && anAbschnittsGrenze(el, "zurueck")) ||
                            (e.key === "Delete" && anAbschnittsGrenze(el, "vor")))
                    ) {
                        e.preventDefault();
                        return;
                    }
                }}
                onBlur={() => {
                    setToken(null);
                    // Die Knöpfe der Bildleiste nehmen keinen Fokus — ein
                    // blur heißt also: weg vom Feld, weg vom Bild.
                    setBildGewaehlt(null);
                    if (timerRef.current) clearTimeout(timerRef.current);
                    // Der Klick IM Menü verhindert sein mousedown-Default und
                    // löst kein blur aus; ein blur heißt also wirklich „weg
                    // vom Feld". AUSNAHME sind die Auswahllisten des
                    // Formatier-Menüs — ein `<select>` darf sein Default nicht
                    // verlieren und nimmt dem Feld dabei den Fokus.
                    void speichern(!menuePos);
                }}
            />

            {/* Die Sprechblasen der Abschnitte — sie tragen die Kommentare aus
                dem Bestand, die an der ganzen Notiz hängen. Sie stehen NEBEN
                dem Feld, nicht darin: im Feld wären sie Text, den man
                versehentlich löschen kann. */}
            {kommentareFuer ? (
                <div className="nf-marken" aria-hidden={false}>
                    {abschnitte.map((a) => {
                        const block = blockVon.get(a.key);
                        if (!block) return null;
                        const faden = kommentareFuer(block);
                        if (!faden.length) return null;
                        return (
                            <span key={a.key} className="nf-marke" data-nk={a.key}>
                                <KommentarMarke
                                    kommentare={faden}
                                    users={users}
                                    titel="Kommentare zu dieser Notiz"
                                    {...(onKommentarNeu && canEdit
                                        ? {
                                              onNeu: (text: string, m: string[], zusatz?: KommentarZusatz) =>
                                                  zusatz
                                                      ? onKommentarNeu(block, text, m, undefined, zusatz)
                                                      : onKommentarNeu(block, text, m),
                                          }
                                        : {})}
                                    {...(onKommentarErledigt ? { onErledigt: onKommentarErledigt } : {})}
                                    {...(kandidaten?.length ? { kandidaten } : {})}
                                    {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                                    {...(kommentarMitAnhang && canEdit ? { mitAnhang: true } : {})}
                                    {...(canEdit ? { aktuellerUserId } : {})}
                                    {...(onKommentarAendern && canEdit
                                        ? { onAendern: onKommentarAendern }
                                        : {})}
                                    {...(onKommentarLoeschen && canEdit
                                        ? { onLoeschen: onKommentarLoeschen }
                                        : {})}
                                />
                            </span>
                        );
                    })}
                </div>
            ) : null}

            {bildGewaehlt && canEdit ? (
                <BildLeiste
                    img={bildGewaehlt}
                    onKleiner={() => bildStufe(-1)}
                    onGroesser={() => bildStufe(1)}
                    onEntfernen={bildEntfernen}
                    {...(onBildOeffnen && bildGewaehlt.hasAttribute("data-bild-id")
                        ? { onOeffnen: () => void oeffneBild(bildGewaehlt) }
                        : {})}
                    onZiehBeginn={bildZiehBeginn}
                    onZiehen={bildZiehen}
                    onZiehEnde={bildZiehEnde}
                    onWeg={() => setBildGewaehlt(null)}
                />
            ) : null}

            {/* Die Leiste über der Markierung — sie nimmt keinen Fokus, sonst
                räumte der Browser die Markierung beim Klick weg. */}
            {markiert && !neuerFaden ? (
                <MarkierLeiste
                    feld={feldRef.current}
                    onKommentar={stelleKommentieren}
                    onErledigt={alsErledigtMarkieren}
                />
            ) : null}

            {/* Der erste Kommentar zu einer frisch markierten Stelle — NEBEN
                ihr, auf ihrer Höhe (PO 28.09.2026: „Kommentarfenster bitte auf
                selber Höhe wie der Kommentar"). Die Stelle ist dabei schon
                hinterlegt, siehe `stelleKommentieren`. */}
            {neuerFaden ? (
                <NebenStelle
                    stelle={() => neuerFaden.stellen.find((s) => s.isConnected)?.getBoundingClientRect() ?? null}
                    feld={feldRef.current}
                    breite={320}
                    className="nb-anker-schreiben"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div className="nb-anker-titel">Kommentar zu dieser Stelle</div>
                    <KommentarEingabe
                        users={users}
                        {...(kandidaten?.length ? { kandidaten } : {})}
                        stil="faden"
                        ariaLabel="Kommentar zu dieser Stelle"
                        placeholder="Kurz sagen, was zu tun ist — @ erwähnt jemanden"
                        autoFocus
                        mitAnhang={!!kommentarMitAnhang}
                        onSenden={ersterKommentar}
                        onAbbrechen={kommentarAbbrechen}
                    />
                </NebenStelle>
            ) : null}

            {/* Der Faden einer markierten Stelle: beim Überfahren zu lesen,
                nach einem Klick zu bedienen. */}
            {offeneStelle && ankerFaeden ? (
                <AnmerkFaden
                    stelle={offeneStelle.el}
                    feld={feldRef.current}
                    onFesthalten={anmerkFesthalten}
                    {...(onKommentarErledigt ? { onErledigt: onKommentarErledigt } : {})}
                    {...(kandidaten?.length ? { kandidaten } : {})}
                    {...(onBezugOeffnen ? { onBezugOeffnen } : {})}
                    {...(kommentarMitAnhang && canEdit ? { mitAnhang: true } : {})}
                    kommentare={
                        ankerFaeden.get(offeneStelle.el.getAttribute("data-anker") ?? "") ?? []
                    }
                    users={users}
                    fest={offeneStelle.fest}
                    onSchliessen={() => setOffeneStelle(null)}
                    {...(onKommentarNeu && canEdit
                        ? {
                              onNeu: async (text: string, mentions: string[], zusatz?: KommentarZusatz) => {
                                  const anker = offeneStelle.el.getAttribute("data-anker");
                                  const key = offeneStelle.el.closest("[data-nk]")?.getAttribute("data-nk");
                                  const block = key ? blockVon.get(key) : null;
                                  if (!anker || !block) return;
                                  await (zusatz
                                      ? onKommentarNeu(block, text, mentions, anker, zusatz)
                                      : onKommentarNeu(block, text, mentions, anker));
                              },
                          }
                        : {})}
                    {...(canEdit ? { aktuellerUserId } : {})}
                    {...(onKommentarAendern && canEdit ? { onAendern: onKommentarAendern } : {})}
                    {...(onKommentarLoeschen && canEdit ? { onLoeschen: onKommentarLoeschen } : {})}
                />
            ) : null}

            {token?.ausloeser === "#" && kandidaten?.length ? (
                <AuswahlAnker feld={feldRef.current}>
                    <BezugPicker
                        kandidaten={kandidaten}
                        query={token.query}
                        onWaehlen={waehlen}
                        onAbbrechen={() => setToken(null)}
                        aktiv={aktiv}
                        onAktiv={setAktiv}
                    />
                </AuswahlAnker>
            ) : null}

            {token?.ausloeser === "@" && users.length ? (
                <AuswahlAnker feld={feldRef.current}>
                    <MentionPicker
                        users={users}
                        query={token.query}
                        onWaehlen={erwaehnen}
                        onAbbrechen={() => setToken(null)}
                        aktiv={aktiv}
                        onAktiv={setAktiv}
                    />
                </AuswahlAnker>
            ) : null}

            {menuePos ? (
                <BlockKontextmenue
                    x={menuePos.x}
                    y={menuePos.y}
                    feld={feldRef.current}
                    {...(medien ? { onBildDatei: (f: File) => void bildEinfuegen(f) } : {})}
                    aktionen={menueAktionen}
                    onClose={() => {
                        menueOffenRef.current = false;
                        setMenuePos(null);
                        // Das beim Bedienen einer Auswahlliste übergangene
                        // `blur` nachholen. `setTimeout`, damit der schließende
                        // Klick seinen Fokuswechsel zuerst abschließt.
                        window.setTimeout(() => {
                            const el = feldRef.current;
                            if (el && aktivesDokument().activeElement !== el) void speichern(true);
                        }, 0);
                    }}
                    onFormatiert={angestossen}
                />
            ) : null}
        </div>
    );
};
